/** The `hosts` value (service.json) that means the bare domain. */
const BARE_DOMAIN_HOST = '@';

/** The GraphQL endpoint path on the API's host (words-happen-gql's graphqlOptions.endpointPath). */
export const API_PATH = '/api';

/**
 * An origin, https://<host>.<domain> (https://<domain> for "@"). `host` is the owning package's service.json
 * "hosts" entry and `domain` its domain for the environment; the same in every environment, local included.
 */
const appOrigin = (appName: string, host: string, domain: string): string => {
  if (!host) {
    throw new Error(`No host to build the ${appName} URL`);
  }
  if (!domain) {
    throw new Error(`No domain to build the ${appName} URL`);
  }

  return host === BARE_DOMAIN_HOST ? `https://${domain}` : `https://${host}.${domain}`;
};

/**
 * Any served host's origin, e.g. https://play-showcase.wordshappen.local for the host 'play-showcase'. Prefer the named
 * helpers below for the apps that link to each other.
 */
export const hostOrigin = (host: string, domain: string): string => appOrigin('app', host, domain);

/** The game app's origin, e.g. https://play.wordshappen.local. */
export const playUrl = (host: string, domain: string): string => appOrigin('game app', host, domain);

/** The identity app's origin, e.g. https://id.wordshappen.local. */
export const identityUrl = (host: string, domain: string): string => appOrigin('identity app', host, domain);

/** The API's origin, where its REST endpoints live (SESSION_PATHS), e.g. https://api.wordshappen.local. */
export const apiOrigin = (host: string, domain: string): string => appOrigin('API', host, domain);

/** The API's base URL, the GraphQL endpoint, e.g. https://api.wordshappen.local/api. */
export const apiUrl = (host: string, domain: string): string => `${apiOrigin(host, domain)}${API_PATH}`;

/** Game app routes other apps link to. */
export const PLAY_PATHS = {
  home: '/',
  /** The landing screen; where signed-out players start. */
  welcome: '/welcome',
} as const;

/**
 * The origins the identity app may send a player back to: the game app's, given its host. Exact origins only; never a
 * subdomain wildcard.
 */
export const allowedReturnOrigins = (playHost: string, domain: string): string[] => [playUrl(playHost, domain)];
